import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const ListComposerBoardListLimitsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"ListComposerBoardListLimits"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"boardId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"board"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"boardId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"limits"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"lists"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"openPerBoard"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"disableAt"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"warnAt"}}]}},{"kind":"Field","name":{"kind":"Name","value":"totalPerBoard"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"disableAt"}},{"kind":"Field","name":{"kind":"Name","value":"status"}},{"kind":"Field","name":{"kind":"Name","value":"warnAt"}}]}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"lists"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"filter"},"value":{"kind":"EnumValue","value":"open"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"ListComposerBoardListLimits","document":ListComposerBoardListLimitsDocument}} as const;
export type ListComposerBoardListLimitsQueryVariables = Types.Exact<{
  boardId: Types.Scalars['ID']['input'];
}>;


export type ListComposerBoardListLimitsQuery = (
  { __typename: 'Query' }
  & { board?: Types.Maybe<(
    { __typename: 'Board' }
    & Pick<Types.Board, 'id'>
    & { limits: (
      { __typename: 'Board_Limits' }
      & { lists: (
        { __typename: 'Board_Limits_Lists' }
        & { openPerBoard: (
          { __typename: 'Limit' }
          & Pick<Types.Limit, 'disableAt' | 'status' | 'warnAt'>
        ), totalPerBoard: (
          { __typename: 'Limit' }
          & Pick<Types.Limit, 'disableAt' | 'status' | 'warnAt'>
        ) }
      ) }
    ), lists: Array<(
      { __typename: 'List' }
      & Pick<Types.List, 'id'>
    )> }
  )> }
);

/**
 * __useListComposerBoardListLimitsQuery__
 *
 * To run a query within a React component, call `useListComposerBoardListLimitsQuery` and pass it any options that fit your needs.
 * When your component renders, `useListComposerBoardListLimitsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useListComposerBoardListLimitsQuery({
 *   variables: {
 *      boardId: // value for 'boardId'
 *   },
 * });
 */
export function useListComposerBoardListLimitsQuery(
  baseOptions: TrelloQueryHookOptions<
    ListComposerBoardListLimitsQuery,
    ListComposerBoardListLimitsQueryVariables
  > &
    (
      | { variables: ListComposerBoardListLimitsQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: ListComposerBoardListLimitsDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    ListComposerBoardListLimitsQuery,
    ListComposerBoardListLimitsQueryVariables
  >(ListComposerBoardListLimitsDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useListComposerBoardListLimitsLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    ListComposerBoardListLimitsQuery,
    ListComposerBoardListLimitsQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    ListComposerBoardListLimitsQuery,
    ListComposerBoardListLimitsQueryVariables
  >(ListComposerBoardListLimitsDocument, options);
}
export function useListComposerBoardListLimitsSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        ListComposerBoardListLimitsQuery,
        ListComposerBoardListLimitsQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    ListComposerBoardListLimitsQuery,
    ListComposerBoardListLimitsQueryVariables
  >(ListComposerBoardListLimitsDocument, options);
}
export type ListComposerBoardListLimitsQueryHookResult = ReturnType<
  typeof useListComposerBoardListLimitsQuery
>;
export type ListComposerBoardListLimitsLazyQueryHookResult = ReturnType<
  typeof useListComposerBoardListLimitsLazyQuery
>;
export type ListComposerBoardListLimitsSuspenseQueryHookResult = ReturnType<
  typeof useListComposerBoardListLimitsSuspenseQuery
>;
export type ListComposerBoardListLimitsQueryResult = Apollo.QueryResult<
  ListComposerBoardListLimitsQuery,
  ListComposerBoardListLimitsQueryVariables
>;
